import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import {
  Button,
  IconButton,
  Tooltip,
  Box,
  CircularProgress,
  Chip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import Typography from '@mui/material/Typography';
import { AlertMessagesContext } from 'react-alert-messages';
import { useTranslation } from 'react-i18next';
import { ShopContext } from 'Context/ShopContext';
import ShopSettingsService from 'services/ShopSettingsService';
import ChargeServices from 'services/ChargeServices';
import styles from './styles.module.css';
import OrderTypeDialog from 'components/popups/AddOrderTypeDialog';
import ConfirmPopup from 'components/utils/Alert/ConfirmPopup';

const DEFAULT_ORDER_TYPES = ['Dine In', 'Take Away', 'Online', 'Delivery'];

const parseConfig = (config) => {
  if (typeof config === 'string') {
    try {
      return JSON.parse(config);
    } catch (e) {
      console.error('Failed to parse config:', e, config);
      return {};
    }
  }
  return config || {};
};


export default function OrderTypes() {
  const { t: tCommon } = useTranslation('common');
  const { t: tManagement } = useTranslation('management');
  const { postAlertMessage } = useContext(AlertMessagesContext);
  const { setShop } = useContext(ShopContext);

  const [isLoading, setLoading] = useState(false);
  const [isSaving, setSaving] = useState(false);
  const [charges, setCharges] = useState([]);
  const [orderType, setOrderType] = useState({});
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editData, setEditData] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);

  const chargesMap = useMemo(() => {
    return charges.reduce((acc, charge) => {
      acc[charge.id] = charge;
      return acc;
    }, {});
  }, [charges]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [chargesList, shopSettings] = await Promise.all([
        ChargeServices.getChargeGroups(),
        ShopSettingsService.getShopSettings(),
      ]);
      setCharges(chargesList || []);
      let config = parseConfig(shopSettings.config);

      let orderTypeConfig = config.order_types || {};

      if (Object.keys(orderTypeConfig).length === 0) {
        const defaultConfig = {};
        DEFAULT_ORDER_TYPES.forEach(type => {
          defaultConfig[type] = [];
        });
        orderTypeConfig = defaultConfig;
      }

      setOrderType(orderTypeConfig);
    } catch (error) {
      postAlertMessage({ text: error.message, type: 'failed' });
    } finally {
      setLoading(false);
    }
  }, [postAlertMessage]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAddOrderType = () => {
    setShowAddDialog(true);
  };

  const handleEditOrderType = (typeName, chargeIds) => {
    setEditData({ name: typeName, charges: chargeIds });
  };

  const handleDialogSuccess = (dialogData) => {
    const newConfig = { ...orderType };
    if (dialogData.oldName && dialogData.oldName !== dialogData.name) {
      delete newConfig[dialogData.oldName];
    }
    newConfig[dialogData.name] = dialogData.charges;
    setOrderType(newConfig);
    handleSave(newConfig);
  };

  const handleRemoveOrderType = (typeName) => {
    setDeleteConfirmation(typeName);
  };

  const confirmDeleteOrderType = async () => {
    if (!deleteConfirmation) return;
    const typeName = deleteConfirmation;
    const newConfig = { ...orderType };
    delete newConfig[typeName];
    setOrderType(newConfig);

    try {
      await handleSave(newConfig);
      setDeleteConfirmation(null);
      postAlertMessage({
        text: tManagement('Order type deleted successfully'),
        type: 'success',
      });
    } catch (error) {
      // Revert local state if save fails
      await loadData();
    }
  };

  const handleSave = async (updatedOrderType = orderType) => {
    setSaving(true);
    try {
      const shopData = await ShopSettingsService.getShopSettings();
      const currentConfig = parseConfig(shopData.config);


      // Ensure we only use 'order_types' (plural) and remove legacy singular key
      if (currentConfig.order_type) {
        delete currentConfig.order_type;
      }

      const payload = {
        ...shopData,
        config: {
          ...currentConfig,
          order_types: updatedOrderType,
        },
      };

      // Clean up fields that might cause issues with stale values or backend-managed logic
      delete payload.updated_at;
      delete payload.created_at;

      const updatedShop = await ShopSettingsService.updateShopSettings(payload);
      setShop(prev => ({ ...prev, ...updatedShop }));

      let config = parseConfig(updatedShop.config);
      setOrderType(config.order_types || {});
    } catch (error) {
      postAlertMessage({ text: error.message, type: 'failed' });
      throw error;
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div className={styles.contentLayout}>
      <div className={styles.settingsGrid}>
        <div className={styles.BillHeaderDiv}>
          <span className={styles.pageHead}>{tCommon('Order Type Management')}</span>
          <div className={styles.filerInputSec}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={handleAddOrderType}
              className={styles.actionBtn}
              style={{ backgroundColor: '#ff851b' }}
            >
              <NoteAddIcon className={styles.actionBtnIcon} />
              {tCommon('Add Order Type')}
            </Button>
          </div>
        </div>

        <div style={{ marginTop: '16px' }}>
          <Box className={styles.tableHead}>
            <Typography className={styles.headerName} style={{ paddingLeft: '20px' }}>{tCommon('Order Type')}</Typography>
            <Typography style={{ flex: 2, fontWeight: 500 }}>{tCommon('Charges')}</Typography>
            <Typography className={styles.headerAction}>{tCommon('Actions')}</Typography>
          </Box>

          {Object.entries(orderType || {}).map(([typeName, chargeIds = []]) => (
            <Box
              key={typeName}
              sx={{
                display: 'flex',
                alignItems: 'center',
                p: 1.5,
                borderBottom: '1px solid #ddd',
                '&:hover': { backgroundColor: '#f9f9f9' },
              }}
            >
              <Typography className={styles.variantName} style={{ flex: 1 }}>
                {typeName}
              </Typography>

              <Box sx={{ flex: 2, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {Array.isArray(chargeIds) && chargeIds.length > 0 ? (
                  chargeIds
                    .map((id) => chargesMap[id])
                    .filter(Boolean)
                    .map((chargeItem) => (
                      <Chip
                        key={chargeItem.id}
                        label={chargeItem.name}
                        size="small"
                        variant="outlined"
                        color="primary"
                      />
                    ))
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    {tCommon('No charges')}
                  </Typography>
                )}
              </Box>

              <Box className={styles.headerAction} style={{ display: 'flex' }}>
                <Tooltip title={tCommon('Edit')}>
                  <IconButton
                    size="small"
                    onClick={() => handleEditOrderType(typeName, chargeIds)}
                    style={{ paddingRight: '10px', color: 'rgb(128, 128, 128)' }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

                <Tooltip title={tCommon('Delete')}>
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveOrderType(typeName)}
                    style={{ paddingRight: '10px', color: 'rgb(128, 128, 128)' }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          ))}
        </div>

        {showAddDialog && (
          <OrderTypeDialog
            handleClose={() => setShowAddDialog(false)}
            handleSuccess={handleDialogSuccess}
            existingNames={Object.keys(orderType)}
            charges={charges}
          />
        )}

        {editData && (
          <OrderTypeDialog
            initialData={editData}
            handleClose={() => setEditData(null)}
            handleSuccess={handleDialogSuccess}
            existingNames={Object.keys(orderType)}
            charges={charges}
          />
        )}

        <ConfirmPopup
          data={!!deleteConfirmation}
          handleClose={() => setDeleteConfirmation(null)}
          handleOkay={confirmDeleteOrderType}
          alertTitle={tCommon('Delete Order Type?')}
          alertContent={tManagement('Are you sure you want to delete order type "{{name}}"?', {
            name: deleteConfirmation,
          })}
          successBtnName={tCommon('Yes, delete')}
          isLoading={isSaving}
        />
      </div>
    </div>
  );
}
